// Skills Gap tab, DA.pdf page 2.
//
// "Skills with Gaps" lists attempted BASE skills, filterable by outcome:
// correct, or one card per misconception type. The summary strip, the cards
// and the row dots all read from `countByOutcome` and `outcomeOf`, so they
// cannot drift apart. The backend still returns `priority` per row and
// `priority_counts`; only `chains_triggered` is consumed, and the rest stays
// so nothing else reading the endpoint breaks.
//
// The mockup's STRAND and SUBSTRAND columns are not rendered yet. They need a
// curriculum-team mapping that does not exist in the data model.
//
// "Mastery by Skill Area" now lives in BmdMasteryBreakdownTab.tsx, on the
// Mastery Breakdown tab, where it drills cluster -> topic -> subtopic.

import React from "react"
// Supplied by context so the tab serves whichever subject the dashboard is showing.
import { useReportFetchers } from "../services/report-fetchers"
import {
  useReportCopy,
  UNTAGGED_KEY,
  type GapOutcome,
} from "../services/report-copy"
import type {
  BmdReportKpi,
  BmdSkillsGapScope,
  BmdSkillGapRow,
  GetStudentSkillsGapResponse,
} from "../types/bmd-report.types"
import {
  abbreviateYearLevel,
  humanMisconception,
  parseYearNumber,
  plainText,
} from "../utils/formatters"

// ============================================================================
// Constants
// ============================================================================

const PRIORITY_FILTER_ALL = "__all__"

type SummaryStripProps = {
  studentName: string
  baseYearLabel: string
  topicCount: number
  attemptedCount: number
  // Everything else on this strip is counted from these, so no two figures on it can disagree.
  rows: BmdSkillGapRow[]
  chainsTriggered: number
}

const SummaryStrip: React.FC<SummaryStripProps> = ({
  studentName,
  baseYearLabel,
  topicCount,
  attemptedCount,
  rows,
  chainsTriggered,
}) => {
  const { gapOutcomes, gapRowNoun } = useReportCopy()
  const outcomeCounts = countByOutcome(rows, gapOutcomes)
  // Counted from the ROWS, like the badges to the right, which is what the props comment has
  // always promised. Taking these from the KPI instead would have this line say "Correct 35"
  // (answers) beside a card reading "Correct (22)" (rows), once a question's parts are folded
  // into one row. The Main Report still reports answers; this tab reports what it lists.
  const correctCount = rows.filter((r) => r.is_correct).length
  const incorrectCount = rows.length - correctCount
  const accuracyPct =
    rows.length > 0 ? Math.round((correctCount / rows.length) * 1000) / 10 : 0
  const initials = studentName
    .split(/\s+/)
    .map((p) => p[0] || "")
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
  return (
    <div className="bg-white rounded shadow-sm p-4 mb-3 d-flex align-items-center flex-wrap gap-3">
      <div
        className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
        style={{
          width: 44,
          height: 44,
          backgroundColor: "#fde2e1",
          color: "#b42318",
        }}
      >
        {initials || "—"}
      </div>
      <div className="flex-grow-1">
        <div className="fw-bold font-size-15 mb-1">{studentName}</div>
        <div className="text-muted font-size-13">
          {baseYearLabel} (Base) · Across {topicCount} topic
          {topicCount === 1 ? "" : "s"} · Attempted {attemptedCount} · Correct{" "}
          {correctCount} of {rows.length}{" "}
          {rows.length === 1 ? gapRowNoun.one : gapRowNoun.many} ({accuracyPct}%) ·
          Incorrect {incorrectCount}
        </div>
      </div>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        {gapOutcomes.map((outcome) => (
          <OutcomeBadge
            key={outcome.key}
            meta={outcome}
            count={outcomeCounts[outcome.key] ?? 0}
          />
        ))}
        <ChainsTriggeredBadge count={chainsTriggered} />
      </div>
    </div>
  )
}

const OutcomeBadge: React.FC<{
  meta: GapOutcome
  count: number
}> = ({ meta, count }) => {
  return (
    // The compact badge has no room for the hint, so it carries it as a tooltip; the big
    // filter card below shows the same line in full.
    <div className="text-center" title={meta.hint}>
      <div className="text-uppercase text-muted font-size-11 fw-semibold">
        {meta.label}
      </div>
      <div
        className="fw-bold font-size-18 mt-1 d-inline-flex align-items-center"
        style={{ color: meta.text }}
      >
        {count}
        <span
          className="rounded-circle ms-2 d-inline-block"
          style={{ width: 8, height: 8, backgroundColor: meta.dot }}
          aria-hidden="true"
        ></span>
      </div>
    </div>
  )
}

const ChainsTriggeredBadge: React.FC<{ count: number }> = ({ count }) => (
  <div className="text-center">
    <div className="text-uppercase text-muted font-size-11 fw-semibold">
      Chains Triggered
    </div>
    <div
      className="fw-bold font-size-18 mt-1 d-inline-flex align-items-center"
      style={{ color: "#1f2a44" }}
    >
      {count}
      <i
        className="fas fa-link ms-2 font-size-13 text-muted"
        aria-hidden="true"
      ></i>
    </div>
  </div>
)

// The cards the tutor filters by: one for correct answers, then one per
// misconception type, so the breakdown matches what the diagnostic recorded
// rather than a derived priority band.
//
// There is deliberately no "untagged" card. All 488 DQs that BMD serves carry
// a misconception on every wrong option, and the 577 untagged DQs are legacy
// content (ids 3501-4100) that BMD's selection rule never picks. If untagged
// answers do appear, they stay reachable under "All" and the card counts
// simply will not sum to the row count.
// A `misconception_type` as marking writes it, or "correct". WHICH ones exist is per subject:
// maths hardcodes three in its schema, English holds its own three as data in
// eng_fdiag_misconception_type. So the buckets, their labels and their colours come from the
// report copy (services/report-copy.tsx) rather than from a table defined here.
type OutcomeKey = string

// A wrong answer whose misconception was never tagged. It should not occur in
// BMD-served content, so it has no card, but the row still needs a dot.
const UNTAGGED_DOT = "#9ca3af"

/** Which card a row belongs to. Correct wins over any misconception tag. */
function outcomeOf(
  row: BmdSkillGapRow,
  outcomes: readonly GapOutcome[],
): OutcomeKey | null {
  if (row.is_correct) return "correct"
  const m = (row.misconception_type || "").toUpperCase()
  if (outcomes.some((outcome) => outcome.key === m)) return m
  // A wrong answer nobody tagged. It belongs in a bucket of its own, or the cards stop adding
  // up to the answers - six of this student's seven wrong answers were untagged and appeared
  // in no card at all. Only where the subject defines the bucket, so the maths card set is
  // untouched.
  return outcomes.some((outcome) => outcome.key === UNTAGGED_KEY) ? UNTAGGED_KEY : null
}

/** Card counts for a row set. One definition, used by the cards and the strip. */
function countByOutcome(
  rows: BmdSkillGapRow[],
  outcomes: readonly GapOutcome[],
): Record<OutcomeKey, number> {
  const counts: Record<OutcomeKey, number> = {}
  for (const outcome of outcomes) counts[outcome.key] = 0
  for (const r of rows) {
    const k = outcomeOf(r, outcomes)
    if (k) counts[k] += 1
  }
  return counts
}

type OutcomeCardsProps = {
  rows: BmdSkillGapRow[]
  active: OutcomeKey | typeof PRIORITY_FILTER_ALL
  onChange: (next: OutcomeKey | typeof PRIORITY_FILTER_ALL) => void
}

const OutcomeCards: React.FC<OutcomeCardsProps> = ({
  rows,
  active,
  onChange,
}) => {
  const { gapOutcomes } = useReportCopy()
  const counts = React.useMemo(
    () => countByOutcome(rows, gapOutcomes),
    [rows, gapOutcomes],
  )

  return (
    <div className="d-flex flex-wrap gap-3 mb-3">
      {gapOutcomes.map((meta) => {
        const key = meta.key
        const isActive = active === key
        return (
          <button
            key={key}
            type="button"
            className="rounded p-3 flex-fill text-start border-0"
            style={{
              minWidth: 200,
              backgroundColor: meta.bg,
              outline: isActive ? `2px solid ${meta.text}` : "none",
              cursor: "pointer",
            }}
            onClick={() => onChange(isActive ? PRIORITY_FILTER_ALL : key)}
            aria-pressed={isActive}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="fw-bold" style={{ color: meta.text }}>
                  {meta.label} ({counts[key] ?? 0})
                </div>
                {meta.hint ? (
                  <div
                    className="font-size-11 mt-1"
                    style={{ color: meta.text, opacity: 0.75 }}
                  >
                    {meta.hint}
                  </div>
                ) : null}
              </div>
              <i
                className="fas fa-chevron-right text-muted font-size-12 mt-1"
                aria-hidden="true"
              ></i>
            </div>
          </button>
        )
      })}
    </div>
  )
}

type SkillsGapTableProps = {
  rows: BmdSkillGapRow[]
  /**
   * How many rows there are before the filters above the table narrow them.
   *
   * NOT a count of skills, despite the wire field it comes from being called
   * `total_skills_in_year`: for English a row is one marked answer, so the same skill appears
   * once per sub-question. The caption takes its noun from the copy context for that reason -
   * it used to read "Showing 42 of 21 attempted skills".
   */
  totalRows: number
}

type SortField = "skill" | "topic" | "year" | "response"
type SortDir = "asc" | "desc"

const FILTER_ALL = "__all__"

const SortHeader: React.FC<{
  field: SortField
  label: string
  width: string
  sortField: SortField | null
  sortDir: SortDir
  onSort: (f: SortField) => void
}> = ({ field, label, width, sortField, sortDir, onSort }) => {
  const isActive = sortField === field
  const iconClass = !isActive
    ? "fas fa-sort"
    : sortDir === "asc"
      ? "fas fa-sort-up"
      : "fas fa-sort-down"
  return (
    <th
      style={{ width, cursor: "pointer", userSelect: "none" }}
      onClick={() => onSort(field)}
      aria-sort={
        !isActive ? "none" : sortDir === "asc" ? "ascending" : "descending"
      }
    >
      <span className="d-inline-flex align-items-center">
        {label}
        <i
          className={`${iconClass} ms-2`}
          style={{
            color: isActive ? "#1f2a44" : "#9ca3af",
            fontSize: 10,
          }}
          aria-hidden="true"
        ></i>
      </span>
    </th>
  )
}

/**
 * What is behind a row that folded several marked parts together.
 *
 * Only drawn when there IS more than one, so an ordinary one-answer row stays as it was. It is
 * what stops "Incorrect" on an 8-item sort reading as though the student got the whole thing
 * wrong - and what explains why one row can stand for eight answers.
 */
const PartTally: React.FC<{ row: BmdSkillGapRow }> = ({ row }) => {
  const total = row.parts_total ?? 1
  if (total <= 1) return null
  return (
    <div className="text-muted font-size-12 mt-1">
      {row.parts_correct ?? 0} of {total} parts correct
    </div>
  )
}

const SkillsGapTable: React.FC<SkillsGapTableProps> = ({
  rows,
  totalRows,
}) => {
  const { gapOutcomes, gapRowNoun, gapTableTitle } = useReportCopy()
  // The same sentence the Untagged card carries, so a row reading "Untagged" can be understood
  // where it is read rather than by scrolling back up to the cards.
  const untaggedHint = gapOutcomes.find((o) => o.key === UNTAGGED_KEY)?.hint
  const [sortField, setSortField] = React.useState<SortField | null>(null)
  const [sortDir, setSortDir] = React.useState<SortDir>("asc")
  const [topicFilter, setTopicFilter] = React.useState<string>(FILTER_ALL)
  const [yearFilter, setYearFilter] = React.useState<string>(FILTER_ALL)
  const [responseFilter, setResponseFilter] = React.useState<string>(FILTER_ALL)

  const topicOptions = React.useMemo(() => {
    const set = new Set<string>()
    for (const r of rows) if (r.topic_name) set.add(r.topic_name)
    return Array.from(set).sort()
  }, [rows])

  const yearOptions = React.useMemo(() => {
    const set = new Set<string>()
    for (const r of rows) if (r.year_level_name) set.add(r.year_level_name)
    // Sort numerically; rows with no parseable year (null) sort last.
    return Array.from(set).sort(
      (a, b) =>
        (parseYearNumber(a) ?? Number.POSITIVE_INFINITY) -
        (parseYearNumber(b) ?? Number.POSITIVE_INFINITY),
    )
  }, [rows])

  // Drop the column filter back to "all" when its selected value is no longer
  // present, which happens when an outcome card or a year-level change shrinks
  // the input set.
  React.useEffect(() => {
    if (
      topicFilter !== FILTER_ALL &&
      !topicOptions.includes(topicFilter)
    ) {
      setTopicFilter(FILTER_ALL)
    }
  }, [topicOptions, topicFilter])
  React.useEffect(() => {
    if (yearFilter !== FILTER_ALL && !yearOptions.includes(yearFilter)) {
      setYearFilter(FILTER_ALL)
    }
  }, [yearOptions, yearFilter])

  const visible = React.useMemo(() => {
    let out = rows.filter((r) => {
      if (topicFilter !== FILTER_ALL && r.topic_name !== topicFilter)
        return false
      if (yearFilter !== FILTER_ALL && r.year_level_name !== yearFilter)
        return false
      if (responseFilter === "correct" && !r.is_correct) return false
      if (responseFilter === "incorrect" && r.is_correct) return false
      return true
    })
    if (sortField) {
      const dir = sortDir === "asc" ? 1 : -1
      out = [...out].sort((a, b) => {
        switch (sortField) {
          case "skill": {
            const av = plainText(a.skill_name).toLocaleLowerCase()
            const bv = plainText(b.skill_name).toLocaleLowerCase()
            return av.localeCompare(bv) * dir
          }
          case "topic": {
            const av = (a.topic_name || "").toLocaleLowerCase()
            const bv = (b.topic_name || "").toLocaleLowerCase()
            return av.localeCompare(bv) * dir
          }
          case "year": {
            // Rows with no parseable year sort last regardless of direction.
            const av = parseYearNumber(a.year_level_name)
            const bv = parseYearNumber(b.year_level_name)
            if (av == null && bv == null) return 0
            if (av == null) return 1
            if (bv == null) return -1
            return (av - bv) * dir
          }
          case "response": {
            // asc: incorrect (0) before correct (1).
            const av = a.is_correct ? 1 : 0
            const bv = b.is_correct ? 1 : 0
            return (av - bv) * dir
          }
        }
      })
    }
    return out
  }, [rows, sortField, sortDir, topicFilter, yearFilter, responseFilter])

  const handleSort = (f: SortField) => {
    if (sortField === f) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortField(f)
      setSortDir("asc")
    }
  }

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="fw-semibold font-size-14">
          {gapTableTitle}
        </div>
        <div className="text-muted font-size-12">
          Showing {visible.length} of {totalRows}{" "}
          {totalRows === 1 ? gapRowNoun.one : gapRowNoun.many}
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <div className="d-flex align-items-center">
          <label
            htmlFor="bmd-skills-topic-filter"
            className="text-muted font-size-12 me-2 mb-0"
          >
            Topic:
          </label>
          <select
            id="bmd-skills-topic-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 160 }}
            value={topicFilter}
            onChange={(e) => setTopicFilter(e.target.value)}
            disabled={topicOptions.length === 0}
          >
            <option value={FILTER_ALL}>All</option>
            {topicOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center">
          <label
            htmlFor="bmd-skills-year-filter"
            className="text-muted font-size-12 me-2 mb-0"
          >
            Year:
          </label>
          <select
            id="bmd-skills-year-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 110 }}
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            disabled={yearOptions.length === 0}
          >
            <option value={FILTER_ALL}>All</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center">
          <label
            htmlFor="bmd-skills-response-filter"
            className="text-muted font-size-12 me-2 mb-0"
          >
            Response:
          </label>
          <select
            id="bmd-skills-response-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 130 }}
            value={responseFilter}
            onChange={(e) => setResponseFilter(e.target.value)}
          >
            <option value={FILTER_ALL}>All</option>
            <option value="correct">Correct</option>
            <option value="incorrect">Incorrect</option>
          </select>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-muted mb-0">
          {rows.length === 0
            ? "No attempted skills in the current selection."
            : "No skills match these filters."}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr className="text-uppercase text-muted fw-bold font-size-11">
                <SortHeader
                  field="skill"
                  label="Skill"
                  width="40%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  field="topic"
                  label="Topic"
                  width="20%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  field="year"
                  label="Required Year Level"
                  width="15%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  field="response"
                  label="Student Response"
                  width="25%"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const outcome = outcomeOf(row, gapOutcomes)
                const dotColor =
                  gapOutcomes.find((o) => o.key === outcome)?.dot ?? UNTAGGED_DOT
                return (
                  <tr key={row.pk_session_question_key}>
                    <td>
                      <div className="d-flex align-items-center">
                        <span
                          className="rounded-circle me-2 d-inline-block"
                          style={{
                            width: 8,
                            height: 8,
                            backgroundColor: dotColor,
                          }}
                          aria-hidden="true"
                        ></span>
                        <span className="fw-semibold">
                          {plainText(row.skill_name) || "Skill (unmapped)"}
                        </span>
                      </div>
                    </td>
                    <td className="font-size-13">
                      {row.topic_name || "—"}
                      {row.sub_topic_name && (
                        <div className="text-muted font-size-12">
                          {row.sub_topic_name}
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: "#fde2e1",
                          color: "#b42318",
                        }}
                      >
                        {abbreviateYearLevel(row.year_level_name)}
                      </span>
                    </td>
                    <td>
                      {row.is_correct ? (
                        <span className="d-inline-flex align-items-center">
                          <i
                            className="fas fa-check-circle me-2"
                            style={{ color: "#16a34a" }}
                            aria-hidden="true"
                          ></i>
                          <span
                            className="fw-semibold"
                            style={{ color: "#15803d" }}
                          >
                            Correct
                          </span>
                        </span>
                      ) : (
                        <span className="d-inline-flex align-items-center">
                          <i
                            className="fas fa-times-circle me-2"
                            style={{ color: "#dc3545" }}
                            aria-hidden="true"
                          ></i>
                          <span className="fw-semibold me-2">Incorrect</span>
                          <span
                            className="text-muted font-size-12"
                            // Only the untagged case gets a tooltip: every other value here is
                            // a named misconception that says what it is.
                            title={
                              row.misconception_type ? undefined : untaggedHint
                            }
                          >
                            {/* The server's own label wins: English holds its misconception
                                names in a table, so renaming one there renames it here.
                                humanMisconception is the maths fallback, which prettifies a
                                code it recognises. */}
                            {row.misconception_label ||
                              humanMisconception(row.misconception_type)}
                          </span>
                        </span>
                      )}
                      <PartTally row={row} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main component
// ============================================================================

export type BmdSkillsGapTabProps = {
  studentKey: string
  yearLevelId: number | undefined
  /** Report scope. null means the whole year level. */
  diagnosticsSessionKey?: string | null
  baseYearLevelName: string | null
  studentName: string
  /**
   * Both KPI objects, not six loose numbers.
   *
   * The strip has to follow the scope selector below it. Passing only the base
   * figures meant switching to Overall changed the table and the cards while
   * the headline above them still counted the base test, which is the exact
   * disagreement this panel keeps being reported for.
   */
  kpi: BmdReportKpi | null
  kpiOverall?: BmdReportKpi | null
}

export const BmdSkillsGapTab: React.FC<BmdSkillsGapTabProps> = ({
  studentKey,
  yearLevelId,
  diagnosticsSessionKey = null,
  baseYearLevelName,
  studentName,
  kpi,
  kpiOverall,
}) => {
  const [data, setData] =
    React.useState<GetStudentSkillsGapResponse | null>(null)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  // Which test the panel counts. Defaults to Overall to match the KPI strip:
  // once a prerequisite test has been sat, the base test is no longer the
  // whole story, and a tutor who never opens the dropdown was reading a
  // partial picture as the total.
  const [scope, setScope] = React.useState<BmdSkillsGapScope>("overall")
  const [outcomeFilter, setOutcomeFilter] = React.useState<
    OutcomeKey | typeof PRIORITY_FILTER_ALL
  >(PRIORITY_FILTER_ALL)

  const { getStudentSkillsGap } = useReportFetchers()
  const { gapOutcomes, noDataForStudent } = useReportCopy()

  React.useEffect(() => {
    if (!studentKey || !yearLevelId) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    getStudentSkillsGap(
      {
        student_key: studentKey,
        year_level_id: yearLevelId,
        diagnostics_session_key: diagnosticsSessionKey,
        scope,
      },
      controller.signal,
    )
      .then((resp) => {
        if (controller.signal.aborted) return
        setData(resp)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load skills gap")
        setLoading(false)
      })
    return () => controller.abort()
  }, [studentKey, yearLevelId, diagnosticsSessionKey, scope])

  const baseYearLabel = abbreviateYearLevel(baseYearLevelName)

  // Falls back to the base figures when the server has not sent an overall
  // set, so an older backend shows the base numbers rather than zeroes.
  const activeKpi = (scope === "overall" && kpiOverall) || kpi
  // Only the two figures that describe the TEST rather than this table's rows still come from
  // the KPI. Correct, incorrect and accuracy are counted from the rows inside the strip, so the
  // headline and the cards beside it can never report different numbers.
  const topicCount = activeKpi?.topics ?? 0
  const attemptedCount = activeKpi?.questions_attempted ?? 0

  // Memoized so SkillsGapTable's own sort and filter useMemos keep stable refs
  // instead of busting on every parent render. It has to sit above the early
  // returns to keep hook order stable.
  const allRows = data?.skills_with_gaps ?? []
  const filteredRows = React.useMemo(() => {
    const rows = data?.skills_with_gaps ?? []
    return outcomeFilter === PRIORITY_FILTER_ALL
      ? rows
      : rows.filter((r) => outcomeOf(r, gapOutcomes) === outcomeFilter)
  }, [data, outcomeFilter, gapOutcomes])

  if (!yearLevelId) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
        {noDataForStudent}
      </div>
    )
  }
  if (loading && !data) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
        Loading skills gap…
      </div>
    )
  }
  if (error) {
    return (
      <div className="bg-white rounded shadow-sm p-5 text-center text-danger">
        {error}
      </div>
    )
  }
  if (!data) return null

  return (
    <>
      {/* Which test the panel counts. Sits above the strip because it changes
          every number below it, not just the table. */}
      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
        <label
          htmlFor="bmd-skills-gap-scope"
          className="text-muted font-size-12 mb-0"
        >
          Showing:
        </label>
        <select
          id="bmd-skills-gap-scope"
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={scope}
          onChange={(e) => setScope(e.target.value as BmdSkillsGapScope)}
        >
          <option value="base">Base test</option>
          <option value="overall">Overall (with prerequisite test)</option>
        </select>
      </div>
      <SummaryStrip
        studentName={studentName}
        baseYearLabel={baseYearLabel}
        topicCount={topicCount}
        attemptedCount={attemptedCount}
        rows={allRows}
        chainsTriggered={data.priority_counts.chains_triggered}
      />
      <OutcomeCards
        rows={allRows}
        active={outcomeFilter}
        onChange={setOutcomeFilter}
      />
      <SkillsGapTable
        rows={filteredRows}
        totalRows={data.total_skills_in_year}
      />
    </>
  )
}

export default BmdSkillsGapTab
